import type { Lang } from '../i18n'
import { PINNED_KEYS, isColumnKey, type ColumnKey } from '../creatives/columns'

export type Theme = 'light' | 'dark'

/**
 * The controlled preference contract AuthBoundary owns and passes down to every
 * screen that reads or changes theme/language, so the shell reuses the same shape
 * rather than inventing its own. AuthBoundary is the sole owner because it outlives
 * every sign-in: a second copy in App would not reach the sign-in screen, so signing
 * out in Russian dark would answer in English light.
 */
export interface ShellPreferences {
  lang: Lang
  theme: Theme
  setLang: (lang: Lang) => void
  toggleTheme: () => void
}

const THEME_KEY = 'grovus.theme'
const LANG_KEY = 'grovus.lang'

/**
 * Theme and language may persist locally; identity/session state must not
 * (design: docs/plans/2026-09-16-gro-12-sign-in-screen-design.md, "Frontend
 * behavior and design system"). Reads and writes are wrapped: private
 * browsing and blocked storage can make `localStorage` throw, and the
 * preference should just fail to persist rather than break rendering.
 */
export function loadTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    // storage unavailable; the preference does not persist this session
  }
}

export function loadLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY)
    return stored === 'ru' || stored === 'uk' ? stored : 'en'
  } catch {
    return 'en'
  }
}

export function saveLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang)
  } catch {
    // storage unavailable; the preference does not persist this session
  }
}

function clientKey(userId: string, field: string): string {
  return `grovus.client.${userId}.${field}`
}

/**
 * The active client, namespaced by user id: a browser shared by two admitted users must
 * not have the second inherit the first's selection.
 */
export function loadActiveClient(userId: string): string | null {
  try {
    return localStorage.getItem(clientKey(userId, 'active'))
  } catch {
    return null
  }
}

export function saveActiveClient(userId: string, id: string): void {
  try {
    localStorage.setItem(clientKey(userId, 'active'), id)
  } catch {
    // storage unavailable; the preference does not persist this session
  }
}

function columnsKey(userId: string): string {
  return `grovus.columns.${userId}.creatives`
}

/**
 * The creatives table's hidden columns, per user on this device (GRO-64).
 *
 * Stored as what is hidden, so a column added by a later build shows. A key this build
 * does not know, a pinned key, and anything that is not a JSON array of strings are
 * dropped rather than trusted: the value outlives the build that wrote it.
 */
export function loadHiddenColumns(userId: string): ColumnKey[] {
  try {
    const raw = localStorage.getItem(columnsKey(userId))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.filter(isColumnKey))].filter((key) => !PINNED_KEYS.includes(key))
  } catch {
    return []
  }
}

export function saveHiddenColumns(userId: string, keys: readonly ColumnKey[]): void {
  try {
    localStorage.setItem(columnsKey(userId), JSON.stringify(keys))
  } catch {
    // storage unavailable; the preference does not persist this session
  }
}

/**
 * The add-client attempts of one user on this device (GRO-82 design, D3).
 *
 * No write here depends on a read. An attempt's record, `attempt.<key>`, is written once,
 * by the page that minted the key, before its first send; every other item is a fact that
 * is only ever set, to a value every writer agrees on. Two tabs can therefore interleave in
 * any order and the stored state is the union of what both wrote, which a single value
 * merged per key could not promise. `dropped.<key>` outlives its attempt for good, as the
 * tombstone a page still holding the key reads before it retries or selects.
 */
export interface StoredAttempt {
  key: string
  name: string
  /** When the minting page recorded it, in ms. */
  at: number
  uncertain: boolean
  dropped: boolean
  outcome: { kind: 'company'; id: string } | { kind: 'none' } | null
}

export type CreationFact = 'uncertain' | 'dropped' | { company: string } | 'void'

const CREATION_FIELDS = ['attempt', 'uncertain', 'dropped', 'company', 'void'] as const

/** Throws on an empty id: `grovus.companyCreate..` or `.undefined.` must never be built. */
export function creationPrefix(userId: string): string {
  if (userId === '') throw new Error('creationPrefix needs a user id')
  return `grovus.companyCreate.${userId}.`
}

function creationItem(userId: string, field: (typeof CREATION_FIELDS)[number], key: string): string {
  return `${creationPrefix(userId)}${field}.${key}`
}

/** False when the record could not be stored: storage refused it, or the key has one already. */
export function recordCreationAttempt(userId: string, key: string, name: string, at: number = Date.now()): boolean {
  if (userId === '') return false
  const item = creationItem(userId, 'attempt', key)
  try {
    if (localStorage.getItem(item) !== null) return false
    localStorage.setItem(item, JSON.stringify({ v: 1, name, at }))
    return true
  } catch {
    return false
  }
}

/** A blind write: it reads nothing, so it cannot undo what another tab wrote. False when not stored. */
export function setCreationFact(userId: string, key: string, fact: CreationFact): boolean {
  if (userId === '') return false
  try {
    if (typeof fact === 'object') localStorage.setItem(creationItem(userId, 'company', key), fact.company)
    else localStorage.setItem(creationItem(userId, fact, key), '1')
    return true
  } catch {
    return false
  }
}

function creationKeys(userId: string): string[] {
  const prefix = creationPrefix(userId)
  const found: string[] = []
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key !== null && key.startsWith(prefix)) found.push(key)
  }
  return found
}

/**
 * Every attempt whose record is stored, with its facts. `null` means storage could not be
 * read, which is not an empty list: a stored attempt may be there, and the caller must not
 * mint a key over it (D4, the inventory state).
 */
export function loadCreationAttempts(userId: string): StoredAttempt[] | null {
  if (userId === '') return null
  try {
    const prefix = creationPrefix(userId)
    const attempts: StoredAttempt[] = []
    for (const item of creationKeys(userId)) {
      const rest = item.slice(prefix.length)
      if (!rest.startsWith('attempt.')) continue
      const key = rest.slice('attempt.'.length)
      const record = attemptRecordOf(localStorage.getItem(item))
      if (record === null) continue
      const company = localStorage.getItem(creationItem(userId, 'company', key))
      const voided = localStorage.getItem(creationItem(userId, 'void', key)) !== null
      attempts.push({
        key,
        name: record.name,
        at: record.at,
        uncertain: localStorage.getItem(creationItem(userId, 'uncertain', key)) !== null,
        dropped: localStorage.getItem(creationItem(userId, 'dropped', key)) !== null,
        outcome: company !== null ? { kind: 'company', id: company } : voided ? { kind: 'none' } : null,
      })
    }
    return attempts.sort((a, b) => a.at - b.at)
  } catch {
    return null
  }
}

function attemptRecordOf(raw: string | null): { name: string; at: number } | null {
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { v, name, at } = parsed as { v?: unknown; name?: unknown; at?: unknown }
    if (v !== 1 || typeof name !== 'string' || typeof at !== 'number') return null
    return { name, at }
  } catch {
    return null
  }
}

/** Whether `key` was dropped, tombstone included; `null` when storage could not be read. */
export function isCreationDropped(userId: string, key: string): boolean | null {
  if (userId === '') return null
  try {
    return localStorage.getItem(creationItem(userId, 'dropped', key)) !== null
  } catch {
    return null
  }
}

/** Settlement: the record first, so no reader sees facts as an attempt, then every fact but the drop. */
export function removeCreationAttempt(userId: string, key: string): void {
  if (userId === '') return
  try {
    localStorage.removeItem(creationItem(userId, 'attempt', key))
    for (const field of ['uncertain', 'company', 'void'] as const) {
      localStorage.removeItem(creationItem(userId, field, key))
    }
  } catch {
    // storage unavailable; the page's memory still holds the settlement
  }
}

/** Removes facts a slower tab wrote after their attempt settled. Never a `dropped.<key>` tombstone. */
export function pruneOrphanFacts(userId: string): void {
  if (userId === '') return
  try {
    const prefix = creationPrefix(userId)
    const items = creationKeys(userId)
    const recorded = new Set(
      items.map((item) => item.slice(prefix.length)).filter((rest) => rest.startsWith('attempt.')).map((rest) => rest.slice('attempt.'.length)),
    )
    for (const item of items) {
      const rest = item.slice(prefix.length)
      const dot = rest.indexOf('.')
      const field = rest.slice(0, dot)
      const key = rest.slice(dot + 1)
      if (field === 'uncertain' || field === 'company' || field === 'void') {
        if (!recorded.has(key)) localStorage.removeItem(item)
      }
    }
  } catch {
    // storage unavailable; orphans are ignored by every read anyway
  }
}
